"use client"

import { useState } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { createClient } from "@/lib/supabase/client"
import { toast } from "sonner"
import Link from "next/link"
import { useSearchParams } from "next/navigation"

export function SignupForm04({ className, ...props }: React.ComponentProps<"div">) {
  const searchParams = useSearchParams()
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState(searchParams.get("email") || "")
  const [otp, setOtp] = useState("")
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")
  const [otpSent, setOtpSent] = useState(false)
  const supabase = createClient()

  const handleSignUp = async () => {
    if (!firstName.trim() || !lastName.trim() || !email) { toast.error("Compila tutti i campi"); return }
    setLoading(true); setMessage("")
    try {
      const res = await fetch("/api/auth/verify-signup", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Registrazione non consentita")
      sessionStorage.setItem("pending_user_data", JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim(), email }))
      const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: true } })
      if (error) throw error
      setOtpSent(true); toast.success("Codice inviato! Controlla la tua email.")
    } catch (err: unknown) {
      const e = err as { message?: string }
      setMessage(e.message || "Errore durante la registrazione"); toast.error(e.message || "Errore")
    } finally { setLoading(false) }
  }

  const handleVerifySignupOtp = async () => {
    if (!otp || otp.length !== 8) { toast.error("Inserisci il codice a 8 cifre"); return }
    setLoading(true); setMessage("")
    try {
      const { data, error } = await supabase.auth.verifyOtp({ email, token: otp, type: "email" })
      if (error) throw error
      const pending = JSON.parse(sessionStorage.getItem("pending_user_data") || "{}")
      if (data.user) {
        const fullName = `${pending.firstName || firstName} ${pending.lastName || lastName}`.trim()
        await supabase.from("users").upsert({ id: data.user.id, email: data.user.email, name: fullName, email_verified: new Date().toISOString() }, { onConflict: "id" })
        sessionStorage.removeItem("pending_user_data")
      }
      toast.success("Account creato! Benvenuto!"); await new Promise(r => setTimeout(r, 500))
      window.location.href = "/dashboard"
    } catch (err: unknown) {
      const e = err as { message?: string }
      setMessage(e.message || "Codice non valido"); toast.error(e.message || "Codice non valido")
    } finally { setLoading(false) }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          {/* Left: form */}
          <div className="p-6 md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <h1 className="text-2xl font-bold">Crea il tuo account</h1>
                <p className="text-muted-foreground text-sm text-balance">Registrati per iniziare a giocare a padel</p>
              </div>
              {message && <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{message}</div>}
              {otpSent ? (
                <FieldGroup>
                  <div className="text-center text-sm text-muted-foreground">Codice inviato a <strong>{email}</strong></div>
                  <Field>
                    <FieldLabel htmlFor="otp-s04">Codice OTP (8 cifre)</FieldLabel>
                    <Input id="otp-s04" type="text" inputMode="numeric" maxLength={8} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} onKeyDown={e => e.key === "Enter" && handleVerifySignupOtp()} placeholder="00000000" className="text-center tracking-widest text-lg" autoFocus />
                  </Field>
                  <Field><Button type="button" onClick={handleVerifySignupOtp} disabled={loading || otp.length !== 8}>{loading ? "Verifica..." : "Verifica e Registrati"}</Button></Field>
                  <div className="flex justify-between text-sm">
                    <button type="button" onClick={() => { setOtpSent(false); setOtp(""); setMessage("") }} className="text-primary hover:underline">← Modifica dati</button>
                    <button type="button" onClick={handleSignUp} disabled={loading} className="text-muted-foreground hover:underline">Nuovo codice</button>
                  </div>
                </FieldGroup>
              ) : (
                <FieldGroup>
                  <div className="grid grid-cols-2 gap-4">
                    <Field>
                      <FieldLabel htmlFor="fn-s04">Nome</FieldLabel>
                      <Input id="fn-s04" placeholder="Mario" value={firstName} onChange={e => setFirstName(e.target.value)} required />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="ln-s04">Cognome</FieldLabel>
                      <Input id="ln-s04" placeholder="Rossi" value={lastName} onChange={e => setLastName(e.target.value)} required />
                    </Field>
                  </div>
                  <Field>
                    <FieldLabel htmlFor="email-s04">Email</FieldLabel>
                    <Input id="email-s04" type="email" placeholder="tua@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
                  </Field>
                  <Field>
                    <Button type="button" onClick={handleSignUp} disabled={loading || !firstName || !lastName || !email}>{loading ? "Invio..." : "Crea Account"}</Button>
                    <FieldDescription className="text-center">Hai già un account? <Link href="/auth/signin" className="underline underline-offset-4">Accedi</Link></FieldDescription>
                  </Field>
                </FieldGroup>
              )}
            </div>
          </div>
          {/* Right: decorative panel */}
          <div className="bg-gradient-to-br from-primary to-primary/70 hidden md:flex flex-col items-center justify-center gap-4 p-8 text-primary-foreground">
            <div className="flex items-center justify-center h-20 w-20 rounded-full bg-primary-foreground/20 backdrop-blur-sm">
              <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <div className="text-center">
              <h2 className="text-xl font-bold">Unisciti a noi</h2>
              <p className="text-sm text-primary-foreground/80 mt-1">Crea il tuo account e inizia a organizzare e partecipare ai tornei di padel.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
